import React from 'react';
import { CheckCircle2, Circle, Loader2, AlertCircle } from 'lucide-react';
import { ProgressReport } from '../types';

interface ProgressPanelProps {
  reports: ProgressReport[];
  isComplete?: boolean;
}

export function ProgressPanel({ reports, isComplete }: ProgressPanelProps) {
  const stages = [
    { id: 'uploading', label: 'Uploading document' },
    { id: 'ocr', label: 'Extracting text (OCR)' },
    { id: 'content_processing', label: 'Processing content' },
    { id: 'generation', label: 'Generating lessons' },
    { id: 'finalizing', label: 'Finalizing course' },
  ];

  // Get latest progress and current stage
  const latestReport = reports[reports.length - 1];
  const currentStageIndex = latestReport ? stages.findIndex(s => s.id === latestReport.stage) : -1;
  const progressPercent = latestReport?.progress_percent || 0;

  return (
    <div className="bg-bg-alt border border-border-main rounded-3xl p-8 shadow-2xl animate-in fade-in zoom-in-95 duration-500">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="text-xl font-black text-text-main tracking-tight">Processing your course</h3>
          <p className="text-text-alt text-sm font-medium mt-1">Our AI is transforming your documents into interactive lessons</p>
        </div>
        {latestReport && !isComplete && (
          <div className="flex items-center gap-3 px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl">
            <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
            <span className="text-xs font-black text-indigo-500 uppercase tracking-widest">{(latestReport.stage || '').replace('_', ' ')}</span>
          </div>
        )}
        {isComplete && (
          <div className="flex items-center gap-3 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-black text-emerald-500 uppercase tracking-widest">Completed</span>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="relative h-3 w-full bg-bg-main rounded-full overflow-hidden mb-12 border border-border-main">
        <div 
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-indigo-600 via-purple-600 to-emerald-500 transition-all duration-1000 ease-out shadow-[0_0_20px_rgba(79,70,229,0.5)]"
          style={{ width: `${progressPercent}%` }}
        />
        {/* Animated pulse for the progress edge */}
        {!isComplete && (
           <div 
             className="absolute top-0 h-full w-20 bg-gradient-to-r from-transparent to-white/20 animate-pulse"
             style={{ left: `calc(${progressPercent}% - 5rem)` }}
           />
        )}
      </div>

      {/* Stages List */}
      <div className="space-y-6">
        {stages.map((stage, index) => {
          const isDone = isComplete || index < currentStageIndex;
          const isCurrent = !isComplete && index === currentStageIndex;
          const stageReport = reports.find(r => r.stage === stage.id);
          const hasError = stageReport?.isError;

          return (
            <div key={stage.id} className={`flex items-start gap-5 transition-all duration-300 ${isDone || isCurrent ? 'opacity-100' : 'opacity-30'}`}>
              <div className="mt-1 shrink-0">
                {isDone ? (
                  <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500 border border-emerald-500/30">
                    <CheckCircle2 className="w-4.5 h-4.5" />
                  </div>
                ) : isCurrent ? (
                  <div className="w-7 h-7 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-500 border border-indigo-500/30">
                    {hasError ? <AlertCircle className="w-4.5 h-4.5 text-rose-500" /> : <Loader2 className="w-4.5 h-4.5 animate-spin" />}
                  </div>
                ) : (
                  <div className="w-7 h-7 rounded-full bg-bg-main flex items-center justify-center text-text-alt opacity-40 border border-border-main">
                    <Circle className="w-4.5 h-4.5" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className={`text-sm font-bold tracking-tight ${isCurrent ? 'text-text-main' : isDone ? 'text-emerald-500' : 'text-text-alt opacity-40'}`}>
                    {stage.label}
                  </h4>
                  {isCurrent && (
                    <span className="text-[10px] font-black text-indigo-500 bg-indigo-500/5 px-2 py-0.5 rounded-lg border border-indigo-500/10 uppercase tracking-tighter">
                      Active
                    </span>
                  )}
                </div>
                {isCurrent && (
                  <p className="text-xs text-text-alt mt-1.5 font-medium animate-in slide-in-from-left-2 transition-colors">
                    {latestReport.message}
                    {latestReport.details && (
                      <span className="block text-[10px] text-text-alt opacity-60 mt-1 uppercase tracking-widest font-black">
                        {latestReport.details}
                      </span>
                    )}
                  </p>
                )}
                {hasError && (
                  <p className="text-xs text-rose-500 mt-1.5 font-medium flex items-center gap-1.5">
                    <AlertCircle className="w-3 h-3" />
                    Some parts could not be processed, continuing...
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isComplete && (
        <div className="mt-12 p-5 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center justify-center gap-3 animate-in slide-in-from-bottom-2">
          <CheckCircle2 className="w-6 h-6 text-emerald-500" />
          <span className="text-sm font-black text-emerald-500 uppercase tracking-widest">Course successfully generated</span>
        </div>
      )}
    </div>
  );
}
